<script setup lang="ts">
// COMPOSABLES
const { tt } = useTypedI18n();
const { isMobile } = useResponsive();

// DATA
const isQuickSearchModalOpen = ref(false);

// Without this Reka focuses the close button and the textarea steals it back a tick later.
const modalContentProps = {
    ...modalNoDescAriaFixupProps.content,
    onOpenAutoFocus: (event: Event): void => event.preventDefault(),
};

// Fullscreen below md, the transparent content *is* the backdrop, so Reka never sees an outside tap.
// A drag that starts in the textarea and ends out here still fires `click` here: only a press that began here closes.
let isBackdropPress = false;

// HANDLERS
const onBackdropPointerdown = (event: PointerEvent): void => {
    isBackdropPress = event.target === event.currentTarget;
};

const onBackdropClick = (event: MouseEvent): void => {
    if (isBackdropPress && event.target === event.currentTarget) isQuickSearchModalOpen.value = false;
};
</script>

<template>
    <!-- Top-aligned at every size: the field grows with the text, so a centred box would move it by half of every height change.
         The `md:` overrides replace UModal's `top-1/2 -translate-y-1/2`; below md (`isMobile`) the modal is fullscreen instead. -->
    <UModal
        v-model:open="isQuickSearchModalOpen"
        :title="tt('search.quick.trigger')"
        :close="false"
        :fullscreen="isMobile"
        :content="modalContentProps"
        :ui="{
            content: 'bg-transparent ring-0 divide-y-0 shadow-none sm:max-w-3xl md:top-[10dvh] md:translate-y-0 md:max-h-[calc(90dvh-2rem)]',
            header: 'sr-only',
            body: 'p-0 sm:p-0',
        }"
    >
        <UButton color="neutral" variant="soft" icon="lucide:search" size="lg">
            <!-- Accessible name below lg, visible label from lg up. -->
            <span class="sr-only lg:not-sr-only">{{ tt('search.quick.trigger') }}</span>
        </UButton>

        <template #body>
            <div
                class="flex min-h-full flex-col items-center justify-start gap-4 px-4 py-6"
                @pointerdown="onBackdropPointerdown"
                @click="onBackdropClick"
            >
                <!-- Pass-through row: only the button is hit, the empty width counts as backdrop. -->
                <div class="pointer-events-none flex w-full justify-end">
                    <UButton
                        icon="lucide:x"
                        color="neutral"
                        variant="ghost"
                        size="lg"
                        square
                        class="bg-default hover:bg-elevated pointer-events-auto shadow-md"
                        :aria-label="tt('search.quick.aria.close')"
                        @click="isQuickSearchModalOpen = false"
                    />
                </div>

                <QuickSearchModalPanel @close="isQuickSearchModalOpen = false" />
            </div>
        </template>
    </UModal>
</template>
